import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Modal,
  Typography,
} from "@mui/material";
import React from "react";
import {
  HomeAssignment,
  ProgressStatus,
} from "../../../../../services/api/types/general/homeAssignment";
import { PrerequisiteSummaryResult } from "../../../../../services/api/types/assignments/assignment";
import { useTranslate } from "../../../../../hooks/useTranslate";

interface PrerequisitesModalColors {
  completed: string;
  inProgress: string;
  primaryButton: string;
  primaryButtonText: string;
  link: string;
}

interface PrerequisitesModalProps {
  open: boolean;
  onClose: () => void;
  assignment: HomeAssignment;
  prerequisites: PrerequisiteSummaryResult[];
  isLoading?: boolean;
  colors: PrerequisitesModalColors;
  onPrerequisiteClick: (prerequisite: PrerequisiteSummaryResult) => void;
}

const isPrerequisiteCompleted = (p: PrerequisiteSummaryResult) =>
  p.progressStatus === ProgressStatus.COMPLETED;

const getPrerequisiteIcon = (
  p: PrerequisiteSummaryResult,
  colors: PrerequisitesModalColors,
) => {
  if (isPrerequisiteCompleted(p)) {
    return (
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: colors.completed,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <CheckIcon sx={{ color: "white", fontSize: 13 }} />
      </Box>
    );
  }

  if (!p.isDirectAssignment) {
    return (
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: "#D0D5DD",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <LockOutlinedIcon sx={{ color: "white", fontSize: 13 }} />
      </Box>
    );
  }

  const isInProgress =
    p.progressStatus === ProgressStatus.INPROGRESS ||
    p.progressStatus === ProgressStatus.INPROGRESS_OVERDUE ||
    p.progressStatus === ProgressStatus.NOTSTARTED_OVERDUE ||
    (p.progressStatus === ProgressStatus.NOTSTARTED &&
      p.completionPercent! > 0);

  if (isInProgress || p.completionPercent! > 0) {
    return (
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: colors.inProgress,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <ScheduleIcon sx={{ color: "white", fontSize: 13 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#D0D5DD",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <LockOutlinedIcon sx={{ color: "white", fontSize: 13 }} />
    </Box>
  );
};

export const PrerequisitesModal: React.FC<PrerequisitesModalProps> = ({
  open,
  onClose,
  assignment,
  prerequisites,
  isLoading,
  colors,
  onPrerequisiteClick,
}) => {
  const t = useTranslate("home");
  const completedCount = prerequisites.filter(isPrerequisiteCompleted).length;
  const totalCount = prerequisites.length;
  const progressValue =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "544px",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0px 8px 8px -4px #10182808",
          outline: "none",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#98A2B3",
              border: "none",
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{ display: "flex", alignItems: "flex-start", gap: 2, pr: 4 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#FEE4E2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LockOutlinedIcon sx={{ color: "#D92D20", fontSize: 24 }} />
            </Box>

            <Box>
              <Typography fontSize={18} fontWeight="600" mb={0.5}>
                {t("modalLockedAssignment", "prerequisitesTitle")}
              </Typography>
              <Typography fontSize={14} color="#101828">
                {t("modalLockedAssignment", "prerequisitesDescription")}
              </Typography>
              <Typography fontSize={14} fontWeight="600" mt={0.5}>
                {assignment.title}
              </Typography>
            </Box>
          </Box>

          {/* Progress */}
          <Box sx={{ mt: 2.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="right"
              mb={0.75}
            >
              {t("modalLockedAssignment", "completedOfTotal", {
                completed: completedCount,
                total: totalCount,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "#EAECF0",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: colors.completed,
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 3, pt: 3 }}>
          <Typography fontSize={14} fontWeight="bold" mb={1.5}>
            {t("modalLockedAssignment", "whatsNeeded")}
          </Typography>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {!isLoading &&
            prerequisites.map((prereq) => {
              const completed = isPrerequisiteCompleted(prereq);

              return (
                <Box key={prereq.userAssignmentUid}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: "8px 16px",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                      }}
                    >
                      {getPrerequisiteIcon(prereq, colors)}
                      <Typography fontSize={14} fontWeight="600" noWrap>
                        {prereq.title}{" "}
                        <Typography
                          component="span"
                          fontSize={14}
                          color="#475467"
                        >
                          (
                          {prereq.isDirectAssignment
                            ? prereq.progress
                            : prereq.completionPercent}
                          %)
                        </Typography>
                      </Typography>
                    </Box>

                    {completed ? (
                      <Typography
                        fontSize={14}
                        color="#475467"
                        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                      >
                        {t("banner", "completed")}
                      </Typography>
                    ) : (
                      <Typography
                        fontSize={12}
                        fontWeight="600"
                        sx={{
                          color: colors.link,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => onPrerequisiteClick(prereq)}
                      >
                        {prereq.isDirectAssignment
                          ? t("modalLockedAssignment", "openAssignment")
                          : t("modalLockedAssignment", "seeRequirements")}{" "}
                        &rarr;
                      </Typography>
                    )}
                  </Box>
                  <Divider />
                </Box>
              );
            })}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 3, pt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={onClose}
            sx={{
              p: "10px 16px",
              borderRadius: "8px",
              backgroundColor: `${colors.primaryButton} !important`,
              color: colors.primaryButtonText,
              fontWeight: 600,
              fontSize: 16,
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            {t("modalLockedAssignment", "close")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
